import type { ModelDefinition } from 'miragejs/-types'
import { Model, Factory, belongsTo } from 'miragejs'

import {
  randPastDate,
  randBoolean,
  randPhrase,
  randParagraph,
  randRecentDate,
} from '@ngneat/falso'

import type { SchemaAttributes } from '../types.js'
import type { MockServer, MirageServer } from './server.js'
import { getExistingEntity, createErrorResponse } from './utils.js'
export type ReminderTemplateData = SchemaAttributes<'ReminderTemplate'>

export const model = (Model as ModelDefinition<ReminderTemplateData>).extend({
  department: belongsTo('department'),
  createdBy: belongsTo('user'),
  modifiedBy: belongsTo('user'),
})

const available_placeholders = [
  '[[client_name]]',
  '[[department_name]]',
  '[[department_phone]]',
  '[[expiry_date]]',
  '[[onlinebooking_url]]',
  '[[patient_name]]',
]

export const factory = Factory.extend<ReminderTemplateData>({
  title() {
    return randPhrase({ maxCharCount: 20 })
  },
  email_text() {
    return randParagraph({ maxCharCount: 100 })
  },
  sms_text() {
    return randParagraph({ maxCharCount: 100 })
  },
  post_text() {
    return randParagraph({ maxCharCount: 100 })
  },
  primary_cta_enabled() {
    return randBoolean()
  },
  primary_cta_text() {
    return randPhrase({ maxCharCount: 20 })
  },
  primary_cta_action() {
    return randPhrase({ maxCharCount: 20 })
  },
  secondary_cta_enabled() {
    return randBoolean()
  },
  secondary_cta_text() {
    return randPhrase({ maxCharCount: 20 })
  },
  secondary_cta_action() {
    return randPhrase({ maxCharCount: 20 })
  },
  update_existing() {
    return randBoolean()
  },
  created() {
    return randPastDate().toISOString()
  },
  modified() {
    return randRecentDate().toISOString()
  },

  afterCreate(reminderTemplate, server) {
    const user = getExistingEntity(server, 'user')
    const department = getExistingEntity(server, 'department')
    reminderTemplate.update({ createdBy: user, modifiedBy: user, department })
  },
})

export function routes(server: MockServer) {
  server.get('/reminder_templates', function (this: MirageServer, schema) {
    const reminderTemplates = schema.all('reminderTemplate')
    const response = this.serialize(reminderTemplates)
    return {
      ...response,
      meta: {
        available_placeholders,
      },
    }
  })

  server.get(
    '/reminder_templates/:id',
    function (this: MirageServer, schema, request) {
      const id = request.params.id
      const reminderTemplate = schema.find('reminderTemplate', id)

      if (!reminderTemplate) {
        return createErrorResponse({
          status: 404,
          headers: { 'Content-Type': 'application/json' },
          errors: [
            {
              detail: 'Reminder template not found.',
            },
          ],
        })
      }

      const response = this.serialize(schema.where('reminderTemplate', { id }))
      const singleItem = response.data[0] as Record<string, unknown>

      // didn't achieve to make this work
      if (singleItem.attributes) {
        const attrs = singleItem.attributes as Record<string, unknown>
        attrs.title = 'Sample Reminder Template'
        attrs.primary_cta_text = 'Primary Action'
        attrs.primary_cta_action = 'primary_action'
        attrs.secondary_cta_text = 'Secondary Action'
        attrs.secondary_cta_action = 'secondary_action'
      }

      return {
        data: singleItem,
        included: response.included || [],
        meta: {
          available_placeholders,
        },
      }
    },
  )

  server.post(
    '/reminder_templates',
    function (this: MirageServer, schema, request) {
      const requestBody = JSON.parse(request.requestBody)
      const attributes = requestBody.data.attributes

      return schema.create('reminderTemplate', attributes)
    },
  )

  server.patch('/reminder_templates/:id', async (schema, request) => {
    const id = request.params.id
    const attrs = JSON.parse(request.requestBody)
    const reminderTemplate = schema.find('reminderTemplate', id)
    if (!reminderTemplate) {
      return createErrorResponse({
        status: 404,
        errors: [
          { detail: `The reminder template with id ${id} was not found` },
        ],
      })
    }
    if (!attrs?.data?.attributes) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Missing "attributes" in request body' }],
      })
    }
    reminderTemplate.update(attrs.data.attributes)
    return {
      data: {
        id: reminderTemplate.id,
        type: 'ReminderTemplate',
        attributes: reminderTemplate.attrs,
      },
    }
  })
}

export function seeds(server: MockServer) {
  server.createList('reminderTemplate', 5)
}
